/**
 * imageCache — proper poster/logo cache using CapacitorHttp on Android.
 *
 * Why a new attempt:
 *   The previous "blob cache" attempt used `fetch(url, { mode: "cors" })`
 *   which silently fails on most provider CDNs (they don't set
 *   `Access-Control-Allow-Origin: *`). The fetch error never populated
 *   the cache, so it was effectively a no-op. This version uses
 *   ``window.Capacitor.Plugins.CapacitorHttp`` on Android — native HTTP,
 *   no CORS enforcement, gets the bytes every time.
 *
 * Design principles (lessons from the previous attempt):
 *
 *   1. **Purely additive.** If anything in the cache layer goes wrong,
 *      the `<img>` MUST still display the original provider URL. We
 *      never replace src with a cache value unless we already have a
 *      working data URL ready. No race conditions, no half-populated
 *      states.
 *
 *   2. **Direct from provider.** Cache fills via CapacitorHttp.get()
 *      on Android (native, no proxy), regular `fetch` elsewhere. Every
 *      byte still comes from the provider's CDN.
 *
 *   3. **Data URLs, not blob URLs.** Blob URLs are tied to the document
 *      lifecycle and can mysteriously revoke. Data URLs are immortal
 *      strings — render-safe across navigations.
 *
 *   4. **Hook-based.** Consumers call ``useCachedImage(url)`` which
 *      returns either the cached data URL or the original URL — never
 *      ``null``. So `<img src={useCachedImage(url)}>` is always valid.
 *
 *   5. **IndexedDB persistence.** Survives app restarts. 150 MB LRU cap.
 */

import { useEffect, useState } from "react";
import { webFetch } from "./webFetch";

const MEM = new Map(); // url -> dataUrl
const INFLIGHT = new Map(); // url -> Promise<dataUrl>
const NEGATIVE = new Set(); // urls we've already failed to cache (skip)
const SUBSCRIBERS = new Map(); // url -> Set<callback>

// v1.0.6 — Persist NEGATIVE across app restarts. Previously the
// negative cache was in-memory only, so every fresh app launch
// re-fetched the same 200+ dead provider URLs → 200+ broken-image
// flashes visible to the user before onError kicked in. Persist in
// localStorage with an LRU cap and 7-day TTL so temporarily-down
// CDNs get retried eventually.
const NEG_LS_KEY = "zapp_image_negative_v1";
const NEG_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const NEG_MAX = 5000;
const _negTs = new Map(); // url -> ts (last failure)
function _loadNegative() {
  try {
    if (typeof localStorage === "undefined") return;
    const raw = localStorage.getItem(NEG_LS_KEY);
    if (!raw) return;
    const now = Date.now();
    const parsed = JSON.parse(raw) || {};
    for (const [url, ts] of Object.entries(parsed)) {
      if (typeof ts === "number" && now - ts < NEG_TTL_MS) {
        NEGATIVE.add(url);
        _negTs.set(url, ts);
      }
    }
  } catch { /* corrupt / disabled — start fresh */ }
}
let _negSaveScheduled = false;
function _saveNegative() {
  if (_negSaveScheduled) return;
  _negSaveScheduled = true;
  setTimeout(() => {
    _negSaveScheduled = false;
    try {
      if (typeof localStorage === "undefined") return;
      // LRU trim: keep newest NEG_MAX entries.
      let entries = Array.from(_negTs.entries());
      if (entries.length > NEG_MAX) {
        entries.sort((a, b) => b[1] - a[1]);
        entries = entries.slice(0, NEG_MAX);
        NEGATIVE.clear();
        _negTs.clear();
        for (const [u, t] of entries) { NEGATIVE.add(u); _negTs.set(u, t); }
      }
      localStorage.setItem(NEG_LS_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch { /* quota / disabled — noop */ }
  }, 2000);
}
function _rememberNegative(url) {
  NEGATIVE.add(url);
  _negTs.set(url, Date.now());
  _saveNegative();
}
_loadNegative();

// Hard concurrency cap so 10,000 mounted <PosterImage>s on Browse don't
// fire 10,000 parallel native HTTP requests — that path used to OOM the
// Android WebView (~800 MB of base64-decoded posters in flight at once).
// Keep this low; CapacitorHttp is fast enough that 4-wide saturates a
// typical provider CDN without crashing the device.
const MAX_CONCURRENCY = 4;
const WAITERS = []; // pending warmImage Promises
let _active = 0;
function _acquire() {
  if (_active < MAX_CONCURRENCY) {
    _active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => WAITERS.push(resolve));
}
function _release() {
  _active -= 1;
  const next = WAITERS.shift();
  if (next) {
    _active += 1;
    next();
  }
}const DB_NAME = "zapp_image_cache_v1";
const STORE = "data";
const META = "meta";
const MAX_BYTES = 1024 * 1024 * 1024;

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("no idb"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function idbGet(store, key) {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(store, "readonly");
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(undefined);
    });
  } catch {
    return undefined;
  }
}

async function idbPut(store, key, value) {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(store, "readwrite");
      tx.objectStore(store).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {/* noop */}
}

async function idbDelete(key) {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction([STORE, META], "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.objectStore(META).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {/* noop */}
}

async function idbListMeta() {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const out = [];
      const tx = db.transaction(META, "readonly");
      const cursorReq = tx.objectStore(META).openCursor();
      cursorReq.onsuccess = (e) => {
        const c = e.target.result;
        if (!c) {
          resolve(out);
          return;
        }
        out.push({ key: c.key, ...c.value });
        c.continue();
      };
      cursorReq.onerror = () => resolve(out);
    });
  } catch {
    return [];
  }
}

let _evicting = false;
async function evictIfOver() {
  if (_evicting) return;
  _evicting = true;
  try {
    const list = await idbListMeta();
    let total = list.reduce((s, m) => s + (m.size || 0), 0);
    if (total <= MAX_BYTES) return;
    list.sort((a, b) => (a.lastAccessAt || 0) - (b.lastAccessAt || 0));
    for (const m of list) {
      if (total <= MAX_BYTES) break;
      await idbDelete(m.key);
      total -= m.size || 0;
      MEM.delete(m.key);
    }
  } finally {
    _evicting = false;
  }
}

// In-memory MEM cap — see frontend-beta/src/lib/imageCache.js for
// the full rationale. tl;dr: prevents the WebView OOM crash when
// scrolling through thousands of covers.
const MAX_MEM_BYTES = 50 * 1024 * 1024;
function _evictMemIfOver() {
  let total = 0;
  for (const v of MEM.values()) total += v.length;
  if (total <= MAX_MEM_BYTES) return;
  for (const key of MEM.keys()) {
    if (total <= MAX_MEM_BYTES) break;
    total -= MEM.get(key).length;
    MEM.delete(key);
  }
}

function _isOurCoverProxy(url) {
  // Match either the Capacitor/Electron prod host OR the browser dev preview.
  if (typeof url !== "string") return false;
  if (url.startsWith("https://zaplayerpro.club/api/covers/")) return true;
  const dev =
    (typeof process !== "undefined" &&
      process.env &&
      (process.env.REACT_APP_PROD_BACKEND_URL || process.env.REACT_APP_BACKEND_URL)) ||
    "";
  return !!dev && url.startsWith(`${dev}/api/covers/`);
}

/** Strip the `?u=` query param off a proxy URL for the in-app log so we
 *  don't dump 200 chars of base64 per failed image. */
export function compactProxyUrl(url) {
  if (typeof url !== "string") return url;
  const i = url.indexOf("/api/covers/");
  if (i === -1) return url;
  const q = url.indexOf("?", i);
  return q === -1 ? url : url.slice(0, q);
}

function isUsableHttpUrl(u) {
  return typeof u === "string" && /^https?:\/\//i.test(u.trim());
}

/**
 * Rewrite a Plex direct-thumbnail URL so the WebView loads it from
 * OUR backend's cover proxy instead of hitting Plex directly. The
 * proxy serves pre-warmed bytes from local disk with a 30-day
 * `Cache-Control` header — instant first paint, persists across
 * navigations / restarts. Non-Plex URLs pass through unchanged.
 *
 * The hash matches `cover_proxy.hash_url()` (SHA-256 of the raw Plex
 * URL string, lower-case hex).
 */
function _sha256Hex(input) {
  function rotr(x, n) { return (x >>> n) | (x << (32 - n)); }
  const K = [
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ];
  let H = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19];
  const bytes = new TextEncoder().encode(input);
  const l = bytes.length;
  const withOne = new Uint8Array(((l + 9 + 63) >>> 6) << 6);
  withOne.set(bytes);
  withOne[l] = 0x80;
  const bitLen = BigInt(l) * 8n;
  const dv = new DataView(withOne.buffer);
  dv.setBigUint64(withOne.length - 8, bitLen, false);
  const W = new Uint32Array(64);
  for (let off = 0; off < withOne.length; off += 64) {
    for (let i = 0; i < 16; i++) W[i] = dv.getUint32(off + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(W[i-15],7) ^ rotr(W[i-15],18) ^ (W[i-15] >>> 3);
      const s1 = rotr(W[i-2],17) ^ rotr(W[i-2],19) ^ (W[i-2] >>> 10);
      W[i] = (W[i-16] + s0 + W[i-7] + s1) >>> 0;
    }
    let [a,b,c,d,e,f,g,h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e,6) ^ rotr(e,11) ^ rotr(e,25);
      const ch = (e & f) ^ ((~e) & g);
      const t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
      const S0 = rotr(a,2) ^ rotr(a,13) ^ rotr(a,22);
      const mj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + mj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H = H.map((x, i) => (x + [a,b,c,d,e,f,g,h][i]) >>> 0);
  }
  return H.map((x) => x.toString(16).padStart(8, "0")).join("");
}

// Backend resolution must match playerApi.resolveBackendUrl():
//  - Android APK (Capacitor) and Electron desktop talk to https://zaplayerpro.club
//  - Browser dev preview talks to REACT_APP_BACKEND_URL
// The cover proxy /api/covers/{hash} HAS to live on the same host that
// served the M3U, otherwise we send Plex requests to a backend that
// hasn't even seen the playlist. This was the silent reason no Plex
// covers were appearing in the shipped APK — fixed in v1.3.17.
const PRODUCTION_BACKEND_URL = "https://zaplayerpro.club";
function _resolveBackend() {
  if (typeof window === "undefined") {
    return (
      (typeof process !== "undefined" &&
        process.env &&
        (process.env.REACT_APP_PROD_BACKEND_URL || process.env.REACT_APP_BACKEND_URL)) ||
      ""
    );
  }
  if (
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  ) {
    return PRODUCTION_BACKEND_URL;
  }
  if (window.electronAPI?.isElectron) return PRODUCTION_BACKEND_URL;
  return (
    (typeof process !== "undefined" &&
      process.env &&
      (process.env.REACT_APP_PROD_BACKEND_URL || process.env.REACT_APP_BACKEND_URL)) ||
    ""
  );
}
const BACKEND = _resolveBackend();

// v1.3.19 — proxy ANY external image URL, not just Plex.
// v1.3.22 — but PASSTHROUGH known-good-CORS image CDNs (TMDB, etc.)
// so they don't depend on the proxy backend at all. Those CDNs send
// proper Access-Control-Allow-Origin headers and load fine from the
// WebView direct — no point burning a round-trip through our proxy
// (which also has to deploy the `?u=` handler to honour them).
//
// Hosts proxied:  Plex, DStv CDN, IPTV provider CDNs, anything else
//                 that fails CORS in the WebView.
// Hosts skipped:  image.tmdb.org (CORS), our own backend (avoid loop),
//                 zaplayerpro.club (same).
const _CORS_FRIENDLY_HOSTS = [
  "image.tmdb.org",
  "www.themoviedb.org",
  "media.themoviedb.org",
  "upload.wikimedia.org",
  "static.wikia.nocookie.net",
];

function _hostOf(url) {
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return "";
  }
}

const _PROXYABLE_RE = /\.(jpg|jpeg|png|webp|gif|bmp)(\?|$|#)/i;
const _PROXYABLE_HINT_RE = /\b(thumb|poster|cover|logo|image|art)\b/i;

function _isOnOurBackend(url) {
  if (!url) return false;
  if (url.startsWith(PRODUCTION_BACKEND_URL)) return true;
  if (BACKEND && url.startsWith(BACKEND)) return true;
  return false;
}

function _shouldProxy(url) {
  if (!isUsableHttpUrl(url)) return false;
  if (_isOnOurBackend(url)) return false;
  const host = _hostOf(url);
  if (_CORS_FRIENDLY_HOSTS.includes(host)) return false;
  // Image-shaped paths OR URLs the catalog hints are art (covers/thumbs).
  return _PROXYABLE_RE.test(url) || _PROXYABLE_HINT_RE.test(url);
}

// Counters surfaced to the in-app debug overlay so we can see at a
// glance how many tvg-logo URLs are flowing through the proxy on the
// current playlist load.
const _stats = { rewritten: 0, passthrough: 0 };
export function getCoverRewriteStats() {
  return { ..._stats };
}
export function resetCoverRewriteStats() {
  _stats.rewritten = 0;
  _stats.passthrough = 0;
}

// btoa(unicode-safe) — base64-encodes the raw URL bytes including
// non-ASCII characters (some provider URLs include unicode).
function _b64utf8(str) {
  try {
    const bytes = new TextEncoder().encode(str);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  } catch {
    try { return btoa(unescape(encodeURIComponent(str))); } catch { return ""; }
  }
}

export function rewritePlexThumbUrl(url) {
  if (!isUsableHttpUrl(url)) return url;
  if (!BACKEND) { _stats.passthrough++; return url; }
  if (!_shouldProxy(url)) { _stats.passthrough++; return url; }
  const trimmed = url.trim();
  const hash = _sha256Hex(trimmed);
  _stats.rewritten++;
  // Pass the raw URL as ?u=<base64> so the proxy can register and
  // fetch it on first miss. URL is opaque to the proxy unless the
  // hash matches (sanity-checked server-side).
  return `${BACKEND}/api/covers/${hash}?u=${encodeURIComponent(_b64utf8(trimmed))}`;
}

function notify(url) {
  const subs = SUBSCRIBERS.get(url);
  if (!subs) return;
  for (const cb of subs) {
    try { cb(); } catch {/* noop */}
  }
}

function subscribe(url, cb) {
  if (!SUBSCRIBERS.has(url)) SUBSCRIBERS.set(url, new Set());
  SUBSCRIBERS.get(url).add(cb);
  return () => {
    const set = SUBSCRIBERS.get(url);
    if (!set) return;
    set.delete(cb);
    if (set.size === 0) SUBSCRIBERS.delete(url);
  };
}

// ---------------------------------------------------------------------------
// Native byte fetcher — CapacitorHttp on Android, regular fetch elsewhere.
// ---------------------------------------------------------------------------

async function fetchImageDataUrl(url) {
  // CapacitorHttp is auto-injected when the plugin is enabled in
  // capacitor.config.json (which it is). On Android this skips the
  // WebView CORS layer and uses native OkHttp.
  const Plugins =
    typeof window !== "undefined" &&
    window.Capacitor &&
    window.Capacitor.Plugins;
  const Http = Plugins && Plugins.CapacitorHttp;
  const isNative =
    typeof window !== "undefined" &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform();

  if (Http && isNative) {
    // CapacitorHttp returns base64 when ``responseType: "blob"``. (Docs
    // are misleading — "blob" yields a base64 string.)
    const r = await Http.request({
      url,
      method: "GET",
      responseType: "blob",
      readTimeout: 30000,
      connectTimeout: 8000,
    });
    if (r.status < 200 || r.status >= 300) {
      throw new Error(`HTTP ${r.status}`);
    }
    const ct =
      (r.headers && (r.headers["Content-Type"] || r.headers["content-type"])) ||
      "image/jpeg";
    if (!ct.startsWith("image/")) {
      throw new Error(`Not an image (${ct})`);
    }
    // r.data is base64
    return { dataUrl: `data:${ct};base64,${r.data}`, size: (r.data.length * 3) / 4 };
  }

  // Web / Electron fallback (v1.4.8 — Web Player routes through backend
  // proxy so cross-origin logo/poster CDNs don't CORS-fail).
  const resp = await webFetch(url, { credentials: "omit", mode: "cors" });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  if (!blob.type.startsWith("image/")) throw new Error("Not an image");
  const buf = await blob.arrayBuffer();
  const u8 = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < u8.length; i += 0x8000) {
    bin += String.fromCharCode.apply(
      null,
      u8.subarray(i, Math.min(i + 0x8000, u8.length)),
    );
  }
  const b64 = btoa(bin);
  return { dataUrl: `data:${blob.type};base64,${b64}`, size: blob.size };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Synchronously read the cached data URL for `url`, or `undefined` if
 * we don't have it yet. The caller's `<img>` should fall back to the
 * original `url` in that case.
 */
export function getCachedImage(url) {
  if (!isUsableHttpUrl(url)) return undefined;
  return MEM.get(url);
}

/**
 * Trigger a background warm of the cache for `url`. Idempotent; safe
 * to call many times concurrently — only one upstream fetch runs.
 * Returns the cached data URL when done, or rejects when the fetch
 * fails (caller can ignore — the `<img>` still works direct).
 */
export function warmImage(url) {
  if (!isUsableHttpUrl(url)) return Promise.resolve(undefined);
  if (MEM.has(url)) return Promise.resolve(MEM.get(url));
  if (NEGATIVE.has(url)) return Promise.reject(new Error("previously failed"));
  if (INFLIGHT.has(url)) return INFLIGHT.get(url);

  const p = (async () => {
    // IndexedDB first.
    try {
      const cached = await idbGet(STORE, url);
      if (typeof cached === "string" && cached.startsWith("data:image/")) {
        MEM.set(url, cached);
        _evictMemIfOver();
        notify(url);
        idbPut(META, url, {
          size: cached.length,
          lastAccessAt: Date.now(),
        }).catch(() => {/* noop */});
        return cached;
      }
    } catch {/* noop */}

    // Hard concurrency gate. With 10k+ mounted PosterImage components
    // on Browse, unbounded parallelism would OOM the Android WebView
    // within seconds (each in-flight base64 poster is ~80 KB; 10 k
    // simultaneously ≈ 800 MB heap → kernel kills the app). We allow
    // only MAX_CONCURRENCY native HTTP requests in flight at a time.
    await _acquire();
    let result;
    try {
      result = await fetchImageDataUrl(url);
    } catch (err) {
      _rememberNegative(url);
      INFLIGHT.delete(url);
      _release();
      throw err;
    }
    _release();
    MEM.set(url, result.dataUrl);
    INFLIGHT.delete(url);
    _evictMemIfOver();
    notify(url);

    // Persist async — never blocks the caller.
    Promise.all([
      idbPut(STORE, url, result.dataUrl),
      idbPut(META, url, {
        size: result.size,
        lastAccessAt: Date.now(),
      }),
    ])
      .then(() => evictIfOver())
      .catch(() => {/* noop */});

    return result.dataUrl;
  })();

  INFLIGHT.set(url, p);
  return p;
}

/**
 * React hook. Returns a renderable string for `<img src>` — ALWAYS
 * defined (never null/undefined) when `url` is a usable HTTP URL.
 *
 *   - On a cache hit, returns the cached data URL → instant render.
 *   - On a miss, returns the original `url` → `<img>` loads direct
 *     from the provider. Behind the scenes we warm the cache so the
 *     NEXT render (after a category navigation) gets the data URL.
 */
export function useCachedImage(url) {
  const [, force] = useState(0);
  const rewritten = rewritePlexThumbUrl(url);

  useEffect(() => {
    if (!isUsableHttpUrl(rewritten)) return undefined;
    // Proxy URLs are served by us with a long Cache-Control —
    // let the WebView's native HTTP cache do the work, don't
    // double-cache into JS heap (the v0.1.20 OOM crash).
    if (_isOurCoverProxy(rewritten)) return undefined;
    if (MEM.has(rewritten) || NEGATIVE.has(rewritten)) return undefined;
    const unsub = subscribe(rewritten, () => force((n) => n + 1));
    warmImage(rewritten).catch(() => {/* the <img> direct src keeps working */});
    return unsub;
  }, [rewritten]);

  if (!isUsableHttpUrl(rewritten)) return rewritten;
  if (_isOurCoverProxy(rewritten)) return rewritten;
  return MEM.get(rewritten) || rewritten;
}

/**
 * Pre-warm a batch of URLs with bounded concurrency. Useful when a
 * category opens and we know the list of posters that will be shown.
 */
export async function warmImageBatch(urls, concurrency = 4) {
  const queue = (urls || []).filter(
    (u) => isUsableHttpUrl(u) && !MEM.has(u) && !NEGATIVE.has(u),
  );
  let i = 0;
  async function worker() {
    while (i < queue.length) {
      const idx = i++;
      try { await warmImage(queue[idx]); } catch {/* noop */}
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, () => worker()),
  );
}

/** Read-only stats for the Settings → Image cache row. */
export function getImageCacheStats() {
  let bytes = 0;
  for (const v of MEM.values()) bytes += v.length;
  return { entries: MEM.size, bytes, failed: NEGATIVE.size };
}

/** Wipe both layers. */
export async function clearImageCache() {
  MEM.clear();
  INFLIGHT.clear();
  NEGATIVE.clear();
  _negTs.clear();
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(NEG_LS_KEY);
    }
  } catch { /* noop */ }
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const tx = db.transaction([STORE, META], "readwrite");
      tx.objectStore(STORE).clear();
      tx.objectStore(META).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {/* noop */}
}
